import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { mealsApi } from '../../../api/meals.api';
import { isAlreadyGone } from '../../../api/client';
import { toast } from '../../ui/Toast';
import type { GroceryItem, InputUnit, Meal, PantryRow } from '../../../types';

export function errorCode(e: unknown): string | undefined {
  return (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
}

/**
 * ADR-0014: the stock is computed by the server, never here. Anything that can
 * move it — an ingredient, a ticked or deleted purchase — calls `refresh`.
 */
export function usePantry(
  tripId: string,
  today: string,
  setMeals: Dispatch<SetStateAction<Meal[]>>,
  setGrocery: Dispatch<SetStateAction<GroceryItem[]>>,
) {
  const { t } = useTranslation();
  const [pantry, setPantry] = useState<PantryRow[]>([]);

  const refresh = useCallback(() => {
    mealsApi.pantry(tripId, today)
      .then(setPantry)
      .catch(() => { /* the plan and the list still work without the stock */ });
  }, [tripId, today]);

  useEffect(() => { refresh(); }, [refresh]);

  async function addIngredient(mealId: string, name: string, amount: number, unit: InputUnit): Promise<boolean> {
    try {
      const ingredient = await mealsApi.addIngredient(tripId, mealId, { name, amount, unit });
      // Upserted server-side: the same product twice is one line with the sum.
      setMeals(prev => prev.map(m => (m.id !== mealId ? m : {
        ...m,
        ingredients: [...m.ingredients.filter(i => i.id !== ingredient.id), ingredient],
      })));
      refresh();
      return true;
    } catch (e) {
      toast(t(errorCode(e) === 'UNIT_MISMATCH' ? 'meals.ingredients.unitMismatch' : 'meals.toast.saveFailed'), 'error');
      return false;
    }
  }

  async function editIngredient(mealId: string, ingredientId: string, amount: number, unit: InputUnit): Promise<boolean> {
    try {
      const saved = await mealsApi.updateIngredient(tripId, mealId, ingredientId, { amount, unit });
      setMeals(prev => prev.map(m => (m.id !== mealId ? m : {
        ...m, ingredients: m.ingredients.map(i => (i.id === saved.id ? saved : i)),
      })));
      refresh();
      return true;
    } catch (e) {
      toast(t(errorCode(e) === 'UNIT_MISMATCH' ? 'meals.ingredients.unitMismatch' : 'meals.toast.saveFailed'), 'error');
      return false;
    }
  }

  async function removeIngredient(mealId: string, ingredientId: string) {
    setMeals(prev => prev.map(m => (m.id !== mealId ? m : {
      ...m, ingredients: m.ingredients.filter(i => i.id !== ingredientId),
    })));
    try {
      await mealsApi.removeIngredient(tripId, mealId, ingredientId);
    } catch (e) {
      if (!isAlreadyGone(e)) toast(t('meals.toast.saveFailed'), 'error');
    }
    refresh();
  }

  async function addMissing(row: PantryRow) {
    try {
      const item = await mealsApi.addMissingToList(tripId, row.id, today);
      setGrocery(prev => [...prev, item]);
      toast(t('meals.pantry.added', { name: row.name }), 'success');
    } catch {
      toast(t('meals.toast.groceryFailed'), 'error');
    }
    refresh();
  }

  return { pantry, refresh, addIngredient, editIngredient, removeIngredient, addMissing };
}
